import { motion } from "framer-motion";
import { ArrowDown, Flame } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Line = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const scrollTo = (id) =>
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      className="relative min-h-screen pt-24 md:pt-28 overflow-hidden"
      data-testid="hero-section"
    >
      {/* gritty cruiser photo with a heavy concrete wash — light + rough */}
      <img
        src="/images/hero-cruiser.jpg"
        alt="Cruiser motorcycle rider on an open highway at golden hour"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#ECECE8]/85" />

      <div className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10 pt-10 md:pt-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-2 border-2 border-[#FF4500]/60 bg-[#FF4500]/10 px-3 py-1.5 rounded-sm mb-6"
          data-testid="hero-badge"
        >
          <Flame className="h-3.5 w-3.5 text-[#FF4500]" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF4500]">
            Street // Dirt // Cruiser
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.82] text-[12vw] md:text-[11vw] lg:text-[9.5vw] text-[#111111]">
          <Line i={0}>Full Throttle.</Line>
          <Line i={1}>
            <span className="text-[#FF4500]">No Limits.</span>
          </Line>
          <Line i={2}>
            <span className="text-stroke">Send It.</span>
          </Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl"
        >
          <p className="text-base md:text-lg font-light text-[#333333] leading-relaxed">
            Built for the wild ones — from street bikes to dirt trails and
            highway cruisers.{" "}
            <span className="text-[#111111] font-medium">Premium heavy-duty moto apparel</span>{" "}
            engineered for the culture.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollTo("#store")}
              data-testid="hero-shop-button"
              className="group inline-flex items-center gap-2 bg-[#FF4500] text-white px-7 py-4 rounded-sm font-black uppercase text-sm tracking-wider hover:bg-[#111111] transition-colors duration-200"
            >
              Shop the Gear
              <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform duration-200" />
            </button>
            <button
              onClick={() => scrollTo("#winners")}
              data-testid="hero-hof-button"
              className="inline-flex items-center gap-2 border-2 border-[#111111] text-[#111111] px-7 py-4 rounded-sm font-bold uppercase text-sm tracking-wider hover:border-[#FF4500] hover:text-[#FF4500] transition-colors duration-200"
            >
              Hall of Fame
            </button>
          </div>
        </motion.div>

        {/* brand stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#111111]/15 border-2 border-[#111111] rounded-sm overflow-hidden max-w-4xl"
          data-testid="hero-stats"
        >
          {[
            { k: "20+", l: "Products in the Drop" },
            { k: "280", l: "GSM Heavyweight Cotton" },
            { k: "For Riders", l: "By Riders" },
            { k: "48HR", l: "Order Dispatch" },
          ].map((s, i) => (
            <div key={i} className="bg-[#FAF9F5] p-5 md:p-6">
              <div className="font-display font-black text-2xl md:text-4xl text-[#FF4500] tracking-tighter">
                {s.k}
              </div>
              <div className="mt-1 text-[10px] md:text-xs uppercase tracking-widest text-[#555555]">
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
